import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import useAuthStore from "../../store/authStore";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ProLockOverlay } from "../../components/ProGate";
import SankeyFunnel from "../../components/SankeyFunnel";
import ApplicationFlow from "../../components/ApplicationFlow";

function MetricCard({ label, value, sub, locked }) {
  return (
    <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 relative overflow-hidden">
      {locked && <ProLockOverlay size="sm" />}
      <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { seekerUser, clearSeekerAuth, refreshSeekerUser } = useAuthStore();
  const IS_PRO = seekerUser?.is_pro ?? false;
  const [flow, setFlow] = useState(null);
  const [freeMetrics, setFreeMetrics] = useState(null);
  const [proMetrics, setProMetrics] = useState(null);
  const [connectedEmployers, setConnectedEmployers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Joborigo: Overview";
  }, []);

  // After Paddle checkout success, refresh user state to pick up is_pro flip.
  // Without this, customer sees stale non-Pro state until they log out and back in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "success") {
      refreshSeekerUser?.();
      // Clean URL so a refresh doesn't re-trigger the refresh.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [refreshSeekerUser]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data: free } = await api.get("/metrics/free");
        setFreeMetrics(free);
        // Separate call so a slow flow computation does not hold up the rest
        // of the dashboard.
        const { data: flowData } = await api.get("/metrics/flow");
        setFlow(flowData);
        const { data: pro } = await api.get("/metrics/pro");
        setProMetrics(pro);
        const { data: connected } = await api.get(
          "/users/me/connected-employers",
        );
        setConnectedEmployers(connected);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearSeekerAuth();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <NavBar currentPage="home" />

      <main className="flex-1 max-w-5xl mx-auto px-6 py-10 w-full">
        {/* Greeting */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1">
            Welcome back
            {seekerUser?.full_name
              ? `, ${seekerUser.full_name.split(" ")[0]}`
              : ""}
          </h1>
          <p className="text-gray-400 text-sm">
            Here's how your job search is going.
          </p>
        </div>

        {/* Upgrade banner for free users */}
        {/* {!IS_PRO && <ProUpgradeBanner />}*/}

        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Metrics grid — 6 cards in 2 rows of 3 */}
            <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-4">
              Your overview
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <MetricCard
                label="Total applications"
                value={freeMetrics?.total_applications ?? 0}
              />
              <MetricCard
                label="Active"
                value={freeMetrics?.active_applications ?? 0}
                sub="in progress"
              />
              <MetricCard
                label="Offers received"
                value={freeMetrics?.offers_received ?? 0}
              />
              <MetricCard
                label="This month"
                value={freeMetrics?.applications_this_month ?? 0}
                sub="applications sent"
              />
              <MetricCard
                label="Response rate"
                value={`${freeMetrics?.avg_response_rate_pct ?? 0}%`}
                sub="of applications replied"
              />
              <MetricCard
                label="Ghost risk"
                value={proMetrics?.ghost_risk_count ?? 0}
                sub="applications going cold"
                locked={!IS_PRO}
              />
            </div>

            {/* Pro — avg salary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <MetricCard
                label="Avg salary expectation"
                value={
                  IS_PRO
                    ? proMetrics?.salary_avg_expectation
                      ? `${proMetrics.salary_avg_expectation.toLocaleString()} EUR`
                      : "—"
                    : "—"
                }
                sub="across your applications"
                locked={!IS_PRO}
              />
            </div>

            {/* Top statuses */}
            <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-4">
              Where your applications went
            </p>
            <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 mb-10">
              <ApplicationFlow data={flow} />
            </div>

            {/* Pro — applications by month */}
            <div className="rounded-2xl border border-gray-100 dark:border-white/10 p-6 bg-white dark:bg-brand-dark-surface relative overflow-hidden mb-10">
              {!IS_PRO && (
                <ProLockOverlay
                  size="md"
                  label="Application trends over time"
                />
              )}
              <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-4">
                Applications per month
              </p>
              <div className="flex items-end gap-3 h-24">
                {(proMetrics?.applications_by_month ?? []).map((m, idx) => {
                  const max = Math.max(
                    ...(proMetrics?.applications_by_month ?? []).map(
                      (x) => x.count,
                    ),
                    1,
                  );
                  const pct = Math.round((m.count / max) * 100);
                  return (
                    <div
                      key={`${m.month}-${idx}`}
                      className="flex-1 flex flex-col items-center gap-1"
                    >
                      <div
                        className="w-full flex items-end justify-center"
                        style={{ height: 72 }}
                      >
                        <div
                          className="w-full bg-brand-violet rounded-t-md transition-all"
                          style={{ height: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 truncate w-full text-center">
                        {m.month.split(" ")[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Connected employers/agencies */}
            {connectedEmployers.length > 0 && (
              <div className="rounded-2xl border border-gray-100 dark:border-white/10 p-6 bg-white dark:bg-brand-dark-surface mb-10">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-4">
                  Companies with you in their pipeline
                </p>
                <div className="flex flex-col gap-3">
                  {connectedEmployers.map((c) => (
                    <div
                      key={c.employer_id + c.application_id}
                      className="flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-brand-violet dark:text-brand-violet-on-dark">
                            {c.company_name?.[0]?.toUpperCase() ?? "?"}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {c.company_name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {c.is_agency ? "Agency" : "Employer"} ·{" "}
                            {c.job_title ?? "Position"}
                          </p>
                        </div>
                      </div>
                      {c.last_status && (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {c.last_status}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
